import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Calendar, Trash2 } from "lucide-react";
import { LOCATION_LABELS } from "@/config/site";
import { API, Loader, ErrorBox, EmptyState } from "./shared";

const STATUS_COLORS = {
  new: "bg-sun-100 text-leaf-900",
  confirmed: "bg-sky-100 text-sky-800",
  completed: "bg-leaf-100 text-leaf-800",
  cancelled: "bg-clay-500/15 text-clay-600",
};

function BookingCard({ booking: b, onStatusChange, onDelete }) {
  return (
    <div
      data-testid={`admin-booking-${b.id}`}
      className="rounded-2xl border-2 border-leaf-100 bg-white p-5 transition-colors hover:border-leaf-300"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-xl font-bold text-leaf-900">{b.name}</h3>
            <span className={`rounded-full px-3 py-0.5 text-xs font-bold uppercase tracking-wide ${STATUS_COLORS[b.status] || STATUS_COLORS.new}`}>
              {b.status}
            </span>
            <span className="rounded-full bg-leaf-50 px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-leaf-700">
              {b.service_type}
            </span>
            {b.location && (
              <span
                data-testid={`admin-booking-location-${b.id}`}
                className="rounded-full bg-sun-100 px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-leaf-800"
              >
                {LOCATION_LABELS[b.location] || b.location}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-leaf-900/70">
            {b.email}
            {b.phone && <span> · {b.phone}</span>}
            {b.preferred_date && <span> · prefers {b.preferred_date}</span>}
          </p>
          {b.bike_info && (
            <p className="mt-1 text-sm"><span className="font-semibold">Bike:</span> {b.bike_info}</p>
          )}
          {b.notes && <p className="mt-1 text-sm text-leaf-900/80">{b.notes}</p>}
          <p className="mt-2 text-xs text-leaf-900/50">
            {new Date(b.created_at).toLocaleString()}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid={`admin-booking-status-${b.id}`}
            value={b.status}
            onChange={(e) => onStatusChange(b.id, e.target.value)}
            className="rounded-full border-2 border-leaf-200 bg-white px-3 py-1.5 text-sm font-semibold text-leaf-900"
          >
            <option value="new">New</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            data-testid={`admin-booking-delete-${b.id}`}
            onClick={() => onDelete(b.id)}
            aria-label="Delete booking"
            className="rounded-full p-2 text-clay-500 hover:bg-clay-500/10"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BookingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [actionErr, setActionErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`, { withCredentials: true });
      setItems(data || []);
      setErr("");
    } catch (e) {
      console.error("Failed to load bookings", e);
      setErr(e?.response?.data?.detail || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setActionErr("");
    try {
      await axios.patch(`${API}/bookings/${id}`, { status }, { withCredentials: true });
      setItems((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch (e) {
      console.error("Failed to update booking status", e);
      setActionErr("Couldn't update status — please refresh and try again.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    setActionErr("");
    try {
      await axios.delete(`${API}/bookings/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((b) => b.id !== id));
    } catch (e) {
      console.error("Failed to delete booking", e);
      setActionErr("Couldn't delete booking — please refresh and try again.");
    }
  };

  if (loading) return <Loader />;
  if (err) return <ErrorBox msg={err} />;
  if (items.length === 0) return <EmptyState icon={Calendar} label="No bookings yet" />;

  return (
    <div data-testid="admin-bookings-list" className="space-y-3">
      {actionErr && <ErrorBox msg={actionErr} />}
      {items.map((b) => (
        <BookingCard key={b.id} booking={b} onStatusChange={updateStatus} onDelete={remove} />
      ))}
    </div>
  );
}
